/**
 * TanStack Query hooks for `advertiser-dashboard-separate-persona`.
 * Thin wrappers over ./api — raw fetch stays in api.ts (D6).
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	list,
	getOne,
	upsert,
	remove,
	advertiserPersonaQueryKeys,
	type AdvertiserPersonaRow,
	type GetAdvertiserPersonaResponse,
	type ListAdvertiserPersonaResponse,
	type UpsertAdvertiserPersonaBody,
	type UpsertAdvertiserPersonaResponse,
} from "./api";

export function useAdvertiserPersonaList(): UseQueryResult<
	ListAdvertiserPersonaResponse,
	Error
> {
	return useQuery({
		queryKey: advertiserPersonaQueryKeys.list(),
		queryFn: list,
	});
}

export function useAdvertiserPersonaDetail(
	advertiser_id: string | null,
): UseQueryResult<GetAdvertiserPersonaResponse, Error> {
	return useQuery({
		queryKey: advertiserPersonaQueryKeys.detail(advertiser_id ?? ""),
		queryFn: () => getOne(advertiser_id ?? ""),
		enabled: !!advertiser_id,
	});
}

export function useAdvertiserPersonaUpsert(): UseMutationResult<
	UpsertAdvertiserPersonaResponse,
	Error,
	UpsertAdvertiserPersonaBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: upsert,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserPersonaQueryKeys.all });
		},
	});
}

export function useAdvertiserPersonaDelete(): UseMutationResult<
	{ ok: true },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserPersonaQueryKeys.all });
		},
	});
}

export type { AdvertiserPersonaRow };
